(() => {
  const header = document.querySelector('#header-component[data-header-04]');
  if (!header) return;
  const collectionPage = document.body.classList.contains('template-collection');
  const collectionHero = collectionPage && document.querySelector('#MainContent .cs-hero');
  if (collectionPage) header.setAttribute('data-header-surface-04', collectionHero ? 'adaptive' : 'solid');
  if (header.getAttribute('data-header-surface-04') === 'solid') return;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const images = new Map();
  let timer;
  let revision = 0;
  const scrollContainer = document.querySelector('.page-wrapper');
  const scrollPosition = () => scrollContainer?.scrollTop || window.scrollY;
  let lastY = scrollPosition();
  let lastWarm = 0;
  // 04 first-paint contrast fix. Country now participates in the per-element contrast sampler.
  const targets = () => [...header.querySelectorAll('.header-logo, .nav04__label, .dropdown-localization__button, .r04-trigger--header, .header-actions__action, .search-action, .nav04__toggle')].filter(node => node.getBoundingClientRect().width > 0);
  const color = value => {
    const match = value.match(/[\d.]+/g);
    if (!match || value === 'transparent') return null;
    const parts = match.map(Number);
    return parts.length < 4 || parts[3] > 0.5 ? parts.slice(0, 3) : null;
  };
  const loadImage = url => {
    if (!images.has(url)) images.set(url, new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = url;
    }));
    return Promise.race([images.get(url), new Promise(resolve => setTimeout(() => resolve(null), 80))]);
  };
  const pixel = (media, rect, style, x, y, background = false) => {
    const width = media.naturalWidth || media.videoWidth;
    const height = media.naturalHeight || media.videoHeight;
    if (!width || !height || !rect.width || !rect.height) return null;
    const fit = background ? style.backgroundSize : style.objectFit;
    const scale = fit === 'cover' ? Math.max(rect.width / width, rect.height / height) : fit === 'contain' ? Math.min(rect.width / width, rect.height / height) : null;
    let drawnWidth = scale ? width * scale : rect.width;
    let drawnHeight = scale ? height * scale : rect.height;
    const position = (background ? style.backgroundPosition : style.objectPosition).split(' ');
    const offset = (value, available) => value?.endsWith('%') ? available * parseFloat(value) / 100 : value === 'left' || value === 'top' ? 0 : value === 'right' || value === 'bottom' ? available : value?.endsWith('px') ? parseFloat(value) : available / 2;
    const sourceX = (x - rect.left - offset(position[0], rect.width - drawnWidth)) / drawnWidth * width;
    const sourceY = (y - rect.top - offset(position[1] || position[0], rect.height - drawnHeight)) / drawnHeight * height;
    if (sourceX < 0 || sourceX >= width || sourceY < 0 || sourceY >= height) return null;
    try {
      context.clearRect(0, 0, 1, 1);
      context.drawImage(media, sourceX, sourceY, 1, 1, 0, 0, 1, 1);
      const result = context.getImageData(0, 0, 1, 1).data;
      return result[3] > 128 ? [...result].slice(0, 3) : null;
    } catch {
      canvas.width = 1;
      return null;
    }
  };
  const sample = async (x, y) => {
    const stack = document.elementsFromPoint(x, y).filter(node => !header.contains(node) && !node.closest('#header-group, .header-section, #shopify-preview-bar, [data-nav04-drawer]'));
    for (const node of stack) {
      const style = getComputedStyle(node);
      if (Number(style.opacity) < 0.5) continue;
      const rect = node.getBoundingClientRect();
      let result;
      if (node instanceof HTMLImageElement) {
        const image = await loadImage(node.currentSrc || node.src);
        if (image) result = pixel(image, rect, style, x, y);
      } else if (node instanceof HTMLVideoElement) {
        result = pixel(node, rect, style, x, y);
        if (!result && node.poster) {
          const poster = await loadImage(node.poster);
          if (poster) result = pixel(poster, rect, style, x, y);
        }
      }
      if (result) return result;
      const url = style.backgroundImage.match(/url\(["']?([^"')]+)["']?\)/)?.[1];
      if (url) {
        const image = await loadImage(url);
        if (image) result = pixel(image, rect, style, x, y, true);
        if (result) return result;
      }
      for (const media of node.querySelectorAll('img, video')) {
        if (header.contains(media)) continue;
        const mediaRect = media.getBoundingClientRect();
        if (x < mediaRect.left || x > mediaRect.right || y < mediaRect.top || y > mediaRect.bottom) continue;
        const mediaStyle = getComputedStyle(media);
        if (mediaStyle.visibility === 'hidden' || Number(mediaStyle.opacity) < 0.5) continue;
        if (media instanceof HTMLImageElement) {
          const image = await loadImage(media.currentSrc || media.src);
          if (image) result = pixel(image, mediaRect, mediaStyle, x, y);
        } else {
          result = pixel(media, mediaRect, mediaStyle, x, y);
        }
        if (result) return result;
      }
      const background = color(style.backgroundColor);
      if (background) return background;
    }
    return [255, 255, 255];
  };
  const settle = async () => {
    const current = ++revision;
    const selected = targets();
    const sampling = Promise.all(selected.map(async node => {
      const rect = node.getBoundingClientRect();
      const points = [0.2, 0.5, 0.8].map(position => sample(Math.min(innerWidth - 1, Math.max(0, rect.left + rect.width * position)), Math.min(innerHeight - 1, Math.max(0, rect.top + rect.height / 2))));
      const pixels = await Promise.all(points);
      const luminance = pixels.reduce((sum, rgb) => sum + (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255, 0) / pixels.length;
      return luminance < 0.5 ? 'white' : 'black';
    }));
    // r04 stable-hero-top: the home hero is dark, so never briefly apply black navigation at scroll position zero while background media is still loading.
    const onHomeHeroTop = document.body.classList.contains('template-index') && scrollPosition() <= 40;
    const values = onHomeHeroTop ? selected.map(() => 'white') : await Promise.race([sampling, new Promise(resolve => setTimeout(() => resolve(selected.map(node => node.dataset.contrastTone04 || 'black')), 250))]);
    if (current !== revision) return;
    selected.forEach((node, index) => {
      node.setAttribute('data-contrast-tone-04', values[index]);
      node.style.setProperty('--rest-foreground-04', values[index] === 'white' ? '#fff' : '#000');
      node.style.setProperty('--rest-logo-filter-04', values[index] === 'white' ? 'brightness(0) invert(1)' : 'brightness(0)');
    });
    header.setAttribute('data-contrast-motion-04', 'rest');
  };
  const stop = () => { clearTimeout(timer); timer = setTimeout(settle, 380); };
  document.addEventListener('scroll', event => {
    if (![scrollContainer, document, document.documentElement, document.body].includes(event.target)) return;
    const position = scrollPosition();
    if (position === lastY) return;
    lastY = position;
    ++revision;
    header.setAttribute('data-contrast-motion-04', 'moving');
    const now = performance.now();
    if (now - lastWarm > 100) {
      lastWarm = now;
      targets().forEach(node => {
        const rect = node.getBoundingClientRect();
        [0.2, 0.5, 0.8].forEach(position => sample(Math.min(innerWidth - 1, Math.max(0, rect.left + rect.width * position)), Math.min(innerHeight - 1, Math.max(0, rect.top + rect.height / 2))));
      });
    }
    stop();
  }, { passive: true, capture: true });
  window.addEventListener('resize', stop, { passive: true });
  window.addEventListener('pageshow', stop);
  header.setAttribute('data-contrast-motion-04', 'rest');
  settle();
  if (document.readyState !== 'complete') window.addEventListener('load', settle, { once: true });
})();

(() => {
  const init = () => {
    const bar = document.querySelector('#header-group .signal-board-04');
    const section = document.querySelector('#header-group > .header-section');
    if (!bar || !section) return;
    let frame;
    const update = () => {
      frame = null;
      const rect = bar.getBoundingClientRect();
      const fixed = getComputedStyle(section).position === 'fixed';
      const offset = fixed ? Math.max(0, rect.bottom) : rect.height;
      const header = section.querySelector('#header-component[data-header-04]');
document.body.style.setProperty('--announcement-header-offset-04', offset + 'px');
if (header) header.style.setProperty('--nav04-bottom', Math.max(0, header.getBoundingClientRect().bottom) + 'px');
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    new ResizeObserver(schedule).observe(bar);
    document.addEventListener('scroll', schedule, {passive:true,capture:true});
    window.addEventListener('resize', schedule, {passive:true});
    document.addEventListener('shopify:section:load', schedule);
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
})();


/* [04] V5 GLOBAL MOSS HUE.
   Overlay only the genuine header logo, never clone interactive navigation.
   The source header uses difference on visual surfaces; this independent hue
   overlay applies Muschio to colored negative areas, exactly like the hero. */
(() => {
  const init = () => {
    const header = document.querySelector('#header-component[data-header-04]');
    const headerSection = document.querySelector('#header-group > .header-section');
    const logo = header?.querySelector('.header-logo');
    if (!header || !headerSection || !logo) return;
    let overlay = document.querySelector('.v5-header-moss-hue');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'v5-header-moss-hue';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('inert','');
      const copy=logo.cloneNode(true);
      copy.removeAttribute('href');
      copy.removeAttribute('id');
      copy.removeAttribute('data-testid');
      copy.setAttribute('tabindex','-1');
      copy.querySelectorAll('[id],[data-testid]').forEach(el => {
        el.removeAttribute('id');
        el.removeAttribute('data-testid');
      });
      overlay.append(copy);
      document.body.append(overlay);
    }
    let raf=0;
    const sync=() => {
      raf=0;
      const closed=header.dataset.headerSurface04==='adaptive' &&
        header.dataset.menuOpen04!=='true' &&
        !document.body.classList.contains('template-cart') &&
        !document.querySelector('[data-nav04-drawer][open], dialog[open]:not(.cart04-dialog)') &&
        !header.querySelector('.nav04--desktop .nav04__label:is(:hover,:focus-visible)');
      const r=logo.getBoundingClientRect();
      const hs=getComputedStyle(headerSection);
      if (!closed || r.width<1 || r.height<1 || r.bottom<=0 || r.top>=innerHeight ||
          hs.visibility==='hidden' || hs.display==='none') {
        overlay.style.visibility='hidden';
        return;
      }
      overlay.style.left=r.left+'px';
      overlay.style.top=r.top+'px';
      overlay.style.width=r.width+'px';
      overlay.style.height=r.height+'px';
      overlay.style.opacity=hs.opacity;
      overlay.style.visibility='visible';
    };
    const request=()=>{if(!raf)raf=requestAnimationFrame(sync)};
    document.addEventListener('scroll',request,{capture:true,passive:true});
    window.addEventListener('scroll',request,{passive:true});
    window.addEventListener('resize',request,{passive:true});
    window.addEventListener('pageshow',request);
    document.addEventListener('pointermove',request,{passive:true});
    document.addEventListener('focusin',request);
    document.addEventListener('click',request);
    const observer=new MutationObserver(request);
    observer.observe(header,{attributes:true,attributeFilter:['data-header-surface-04','data-menu-open-04','data-sticky-state','data-scroll-direction']});
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-nd04-brand']});
    request();
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
})();
